import React, { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { disposeScene } from '../utils/ThreeCleanup';
import SoundManager from '../utils/SoundManager';

// Interactive summit map: a real low-poly 3D mountain range (one peak per
// expedition, snow-capped, sized by altitude) with a list + detail panel.
// Ported from the gb4k.fr V2 Summit and given an actual 3D model.

interface Peak {
  id: string; name: string; altitude: number; reached?: number;
  status: 'success' | 'failed' | 'planned'; year?: number; country: string; difficulty: number; story: string;
}

const PEAKS: Peak[] = [
  { id: 'lenin', name: 'Peak Lenin', altitude: 7134, reached: 6400, status: 'failed', year: 2024, country: 'Kyrgyzstan', difficulty: 10,
    story: 'Severe weather forced retreat at 6400 m. The mountain teaches humility. Sometimes the greatest lesson is knowing when to turn back — respecting nature’s power and the value of a strategic withdrawal.' },
  { id: 'kilimanjaro', name: 'Mount Kilimanjaro', altitude: 5895, status: 'success', year: 2022, country: 'Tanzania', difficulty: 7,
    story: 'Africa’s highest peak, conquered. The journey through multiple climate zones — from rainforest to arctic — gave invaluable insight into acclimatization strategy and team coordination at altitude.' },
  { id: 'elbrus', name: 'Mount Elbrus', altitude: 5642, status: 'planned', country: 'Russia', difficulty: 7,
    story: 'Europe’s highest peak. A dormant volcano in the Caucasus. Brutal weather and a long summit push will test physical and mental limits — a crucial milestone in building expedition capability.' },
  { id: 'ararat', name: 'Mount Ararat', altitude: 5137, reached: 4400, status: 'failed', year: 2023, country: 'Turkey', difficulty: 8,
    story: 'Reached 4400 m before weather and logistics forced retreat. The mountain remains unconquered, but the experience — and the hypoxic training protocols tested — was invaluable. A return is planned.' },
  { id: 'stanley', name: 'Mount Stanley (Margherita)', altitude: 5109, status: 'success', year: 2021, country: 'Uganda / DRC', difficulty: 9,
    story: 'Third highest peak in Africa. Technical climbing through glaciers at the equator, with humanitarian connections deepened through 3Causes operations in the region. Mountaineering meets mission.' },
  { id: 'blanc', name: 'Mont Blanc', altitude: 4808, status: 'planned', country: 'France', difficulty: 6,
    story: 'The roof of Western Europe. A classic alpine ascent via the Goûter route — spectacular views, challenging conditions, and a gateway to higher-altitude mountaineering.' },
  { id: 'fuji', name: 'Mount Fuji', altitude: 3776, status: 'planned', country: 'Japan', difficulty: 4,
    story: 'Japan’s sacred mountain. A cultural and spiritual journey as much as a physical one — the most-climbed mountain in the world, where natural beauty meets human devotion.' },
];

const STATUS: Record<Peak['status'], { label: string; cls: string; col: number }> = {
  success: { label: 'SUMMIT REACHED', cls: 'ok', col: 0x5fe979 },
  failed: { label: 'TURNED BACK', cls: 'bad', col: 0xff6b6b },
  planned: { label: 'PLANNED', cls: 'plan', col: 0x39e6ff },
};
const MAXALT = 7134;

// deterministic noise for rugged peaks
function rng(seed: number) { return () => { seed = (seed + 0x6d2b79f5) | 0; let t = Math.imul(seed ^ (seed >>> 15), 1 | seed); t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t; return ((t ^ (t >>> 14)) >>> 0) / 4294967296; }; }

// Build a rugged snow-capped mountain as one vertex-coloured mesh.
function makeMountain(height: number, radius: number, seed: number, snowLine: number): THREE.Mesh {
  const geo = new THREE.ConeGeometry(radius, height, 9, 6, false);
  const r = rng(seed);
  const pos = geo.attributes.position as THREE.BufferAttribute;
  const rock = new THREE.Color(0x5b5148), rockDark = new THREE.Color(0x2e2822), snow = new THREE.Color(0xf2f6ff);
  const colors: number[] = [];
  const v = new THREE.Vector3();
  for (let i = 0; i < pos.count; i++) {
    v.fromBufferAttribute(pos, i);
    const yn = (v.y + height / 2) / height; // 0 base .. 1 tip
    if (yn > 0.02 && yn < 0.98) { const j = (r() - 0.5) * radius * 0.28; v.x += j; v.z += (r() - 0.5) * radius * 0.28; v.y += (r() - 0.5) * height * 0.05; }
    pos.setXYZ(i, v.x, v.y, v.z);
    const c = yn > snowLine ? snow.clone() : rock.clone().lerp(rockDark, r() * 0.5);
    if (yn > snowLine) c.lerp(rock, Math.max(0, (snowLine + 0.08 - yn) * 4)); // soft snow line
    colors.push(c.r, c.g, c.b);
  }
  geo.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
  geo.computeVertexNormals();
  const mat = new THREE.MeshStandardMaterial({ vertexColors: true, flatShading: true, roughness: 0.95, metalness: 0.0 });
  return new THREE.Mesh(geo, mat);
}

const SummitVisualization: React.FC = () => {
  const mountRef = useRef<HTMLDivElement>(null);
  const [sel, setSel] = useState('lenin');
  const selRef = useRef(sel);
  useEffect(() => { selRef.current = sel; }, [sel]);
  const apiRef = useRef<{ focus: (id: string) => void } | null>(null);

  useEffect(() => {
    const mount = mountRef.current!;
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x0a1420);
    scene.fog = new THREE.Fog(0x14324a, 34, 90);

    const camera = new THREE.PerspectiveCamera(52, mount.clientWidth / mount.clientHeight, 0.1, 400);
    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(mount.clientWidth, mount.clientHeight);
    mount.appendChild(renderer.domElement);

    // sky gradient dome
    const sky = new THREE.Mesh(
      new THREE.SphereGeometry(200, 24, 12),
      new THREE.ShaderMaterial({
        side: THREE.BackSide,
        uniforms: { top: { value: new THREE.Color(0x1b3b66) }, bot: { value: new THREE.Color(0xe9a06a) } },
        vertexShader: 'varying float h; void main(){ h = normalize(position).y; gl_Position = projectionMatrix * modelViewMatrix * vec4(position,1.0); }',
        fragmentShader: 'varying float h; uniform vec3 top; uniform vec3 bot; void main(){ gl_FragColor = vec4(mix(bot, top, clamp(h*1.4+0.15,0.0,1.0)), 1.0); }',
      }),
    );
    scene.add(sky);

    scene.add(new THREE.AmbientLight(0x9fb6d0, 0.7));
    const sun = new THREE.DirectionalLight(0xfff0d8, 1.4); sun.position.set(-8, 10, 6); scene.add(sun);
    const rim = new THREE.DirectionalLight(0x88bbff, 0.4); rim.position.set(6, 4, -8); scene.add(rim);

    // ground
    const ground = new THREE.Mesh(
      new THREE.CircleGeometry(60, 48),
      new THREE.MeshStandardMaterial({ color: 0x101c16, roughness: 1 }),
    );
    ground.rotation.x = -Math.PI / 2; ground.position.y = -0.02; scene.add(ground);
    const grid = new THREE.GridHelper(120, 48, 0x245032, 0x143020); (grid.material as THREE.Material).opacity = 0.35; (grid.material as THREE.Material).transparent = true; scene.add(grid);

    // mountains on an arc
    const group = new THREE.Group(); scene.add(group);
    const peakObjs: { id: string; mesh: THREE.Mesh; base: THREE.Vector3; marker: THREE.Mesh }[] = [];
    const N = PEAKS.length;
    PEAKS.forEach((p, i) => {
      const a = (i / (N - 1) - 0.5) * Math.PI * 0.9;
      const R = 15;
      const x = Math.sin(a) * R, z = -Math.cos(a) * R + 6;
      const h = 3 + (p.altitude / MAXALT) * 9;
      const rad = 2.4 + (p.difficulty / 10) * 1.2;
      const snowLine = p.status === 'planned' ? 0.72 : 0.58 - (p.altitude / MAXALT) * 0.15;
      const m = makeMountain(h, rad, i + 1, snowLine);
      m.position.set(x, h / 2, z);
      m.userData.id = p.id;
      group.add(m);
      // status ring marker at base
      const ring = new THREE.Mesh(new THREE.RingGeometry(rad + 0.5, rad + 0.9, 24),
        new THREE.MeshBasicMaterial({ color: STATUS[p.status].col, side: THREE.DoubleSide, transparent: true, opacity: 0.8 }));
      ring.rotation.x = -Math.PI / 2; ring.position.set(x, 0.05, z); scene.add(ring);
      peakObjs.push({ id: p.id, mesh: m, base: new THREE.Vector3(x, h / 2, z), marker: ring });
    });

    // camera framing
    const camTarget = new THREE.Vector3(0, 4, 6);
    let camAngle = 0, wantAngle = 0, wantDist = 30, dist = 30;
    const applyFocus = (id: string) => {
      const idx = PEAKS.findIndex((p) => p.id === id); if (idx < 0) return;
      const a = (idx / (N - 1) - 0.5) * Math.PI * 0.9;
      wantAngle = a * 0.6; wantDist = 22;
    };
    apiRef.current = { focus: applyFocus };
    applyFocus(selRef.current);

    const ray = new THREE.Raycaster(); const mouse = new THREE.Vector2();
    const onClick = (e: MouseEvent) => {
      const rect = renderer.domElement.getBoundingClientRect();
      mouse.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      mouse.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
      ray.setFromCamera(mouse, camera);
      const hit = ray.intersectObjects(peakObjs.map((o) => o.mesh))[0];
      if (hit) { const id = (hit.object.userData.id as string); try { SoundManager.playSound('toggle'); } catch { /* noop */ } setSel(id); }
    };
    renderer.domElement.addEventListener('pointerdown', onClick);

    const clock = new THREE.Clock();
    let raf = 0;
    const animate = () => {
      raf = requestAnimationFrame(animate);
      const dt = clock.getDelta(), t = clock.elapsedTime;
      wantAngle += dt * 0.05; // slow drift
      camAngle += (wantAngle - camAngle) * Math.min(1, dt * 2);
      dist += (wantDist - dist) * Math.min(1, dt * 2);
      camera.position.set(Math.sin(camAngle) * dist, 9 + Math.sin(t * 0.2) * 0.6, Math.cos(camAngle) * dist + 6);
      camera.lookAt(camTarget);
      // highlight selected peak
      for (const o of peakObjs) {
        const on = o.id === selRef.current;
        const targetY = o.base.y + (on ? 0.6 + Math.sin(t * 2) * 0.15 : 0);
        o.mesh.position.y += (targetY - o.mesh.position.y) * Math.min(1, dt * 4);
        (o.marker.material as THREE.MeshBasicMaterial).opacity = on ? 0.95 : 0.4;
        o.marker.scale.setScalar(on ? 1.1 + Math.sin(t * 3) * 0.05 : 1);
        (o.mesh.material as THREE.MeshStandardMaterial).emissive?.setHex(on ? 0x223344 : 0x000000);
      }
      renderer.render(scene, camera);
    };
    animate();

    const onResize = () => {
      if (!mount.clientWidth) return;
      camera.aspect = mount.clientWidth / mount.clientHeight; camera.updateProjectionMatrix();
      renderer.setSize(mount.clientWidth, mount.clientHeight);
    };
    window.addEventListener('resize', onResize);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', onResize);
      renderer.domElement.removeEventListener('pointerdown', onClick);
      disposeScene(scene, renderer);
      if (renderer.domElement.parentNode) renderer.domElement.parentNode.removeChild(renderer.domElement);
      apiRef.current = null;
    };
  }, []);

  useEffect(() => { apiRef.current?.focus(sel); }, [sel]);

  const p = PEAKS.find((x) => x.id === sel)!;
  const okCount = PEAKS.filter((x) => x.status === 'success').length;

  return (
    <div className="summit3d">
      <div ref={mountRef} className="summit3d-canvas" />
      <div className="summit-ui-inner">
        <div className="summit-list" role="list">
          <div className="summit-head">EXPEDITIONS · {okCount} SUMMITS · {PEAKS.length} PEAKS</div>
          {PEAKS.map((x) => (
            <button key={x.id} role="listitem" className={`summit-row ${x.id === sel ? 'on' : ''}`}
              onClick={() => { try { SoundManager.playSound('toggle'); } catch { /* noop */ } setSel(x.id); }}>
              <span className={`dot ${STATUS[x.status].cls}`} />
              <span className="sname">{x.name}</span>
              <span className="salt">{x.altitude} m</span>
              <span className="bar"><span className="fill" style={{ width: `${(x.altitude / MAXALT) * 100}%` }} /></span>
            </button>
          ))}
        </div>
        <div className="summit-detail">
          <h2>{p.name}</h2>
          <div className={`summit-status ${STATUS[p.status].cls}`}>{STATUS[p.status].label}{p.year ? ` · ${p.year}` : ''}</div>
          <dl className="summit-stats">
            <div><dt>Altitude</dt><dd>{p.altitude} m</dd></div>
            {p.reached && <div><dt>Reached</dt><dd>{p.reached} m</dd></div>}
            <div><dt>Country</dt><dd>{p.country}</dd></div>
            <div><dt>Difficulty</dt><dd>{p.difficulty}/10</dd></div>
          </dl>
          <p className="summit-story">{p.story}</p>
        </div>
      </div>
    </div>
  );
};

export default SummitVisualization;
